import { TransportError } from 'ProdantixSdk/core/errors';
import { type FetchLike, makeAbortController, resolveFetch, trimTrailingSlash } from 'ProdantixSdk/core/http';

export const FORM_VIEWED = '$form_viewed';
export const FORM_STARTED = '$form_started';

export type FormFieldKind = 'checkbox' | 'checkboxes' | 'date' | 'dropdown' | 'email' | 'file' | 'hidden' | 'number' | 'phone' | 'radio' | 'rich_text' | 'text' | 'textarea';

export interface FormOption {
  archived?: boolean;
  label: string;
  value: string;
}

export interface FormCondition {
  field: string;
  op: 'equals' | 'is_set' | 'one_of';
  values: string[];
}

export interface FormFieldRules {
  maxLength?: number | null;
  maximum?: number | null;
  minLength?: number | null;
  minimum?: number | null;
}

export type FormHidden = { source: 'cookie'; value: string } | { source: 'fixed'; value: string } | { source: 'query'; value: string };

export interface FormField {
  condition?: FormCondition | null;
  help: string;
  hidden?: FormHidden | null;
  key: string;
  kind: FormFieldKind;
  label: string;
  options: FormOption[];
  placeholder: string;
  required: boolean;
  rules: FormFieldRules;
}

export interface FormStep {
  fields: FormField[];
  title: string;
}

export type FormAfterSubmit = { kind: 'message'; value: string } | { kind: 'redirect'; value: string };

export interface FormConsentPurpose {
  key: string;
  label: string;
  notice: string;
}

export interface FormConsent {
  processing?: string | null;
  purposes: FormConsentPurpose[];
}

export interface FormStyle {
  background: string;
  button: string;
  buttonText: string;
  fieldBackground: string;
  fieldText: string;
  font: string;
  radius: number;
  text: string;
  width: number;
}

export interface FormChallenge {
  difficulty: number;
  issuedAt: number;
  nonce: string;
  signature: string;
}

export interface ServedForm {
  /** Whether the form takes responses this month under the plan. */
  accepting: boolean;
  afterSubmit: FormAfterSubmit;
  challenge: FormChallenge | null;
  consent: FormConsent;
  honeypot: string;
  id: string;
  /** Whether the form carries the "Powered by Prodantix" line. */
  poweredBy: boolean;
  steps: FormStep[];
  style: FormStyle;
  version: number;
}

export interface FormSubmission {
  answers: Record<string, unknown>;
  challenge: FormChallenge;
  distinctId: string;
  isAnonymous: boolean;
  /** The landing page the form sits on, sent with `pageVariant`. */
  pageId?: string;
  pageUrl: string;
  pageVariant?: string;
  solution: string;
}

export interface FieldRefusal {
  field: string;
  rule: string;
}

/** A submission the edge refused, with each refused field when it named them. */
export class FormRefusedError extends Error {
  readonly fields: FieldRefusal[];
  readonly status: number;

  constructor(status: number, fields: FieldRefusal[], message: string) {
    super(message);
    this.name = 'FormRefusedError';
    this.status = status;
    this.fields = fields;
  }
}

export interface FormsClientOptions {
  fetch?: FetchLike;
  host: string;
  projectKey: string;
  requestTimeoutMs?: number;
}

export class FormsClient {
  private readonly fetch?: FetchLike;
  private readonly host: string;
  private readonly projectKey: string;
  private readonly requestTimeoutMs: number;

  constructor(options: FormsClientOptions) {
    this.fetch = resolveFetch(options.fetch);
    this.host = trimTrailingSlash(options.host);
    this.projectKey = options.projectKey;
    this.requestTimeoutMs = options.requestTimeoutMs ?? 10_000;
  }

  private async request(path: string, init: { body?: string; method: 'GET' | 'POST' }): Promise<{ status: number; text: string }> {
    const fetchImpl = this.fetch;
    if (!fetchImpl) {
      throw new TransportError('no fetch implementation available in this runtime');
    }
    const controller = makeAbortController();
    const timer = controller ? setTimeout(() => controller.abort(), this.requestTimeoutMs) : undefined;
    try {
      const headers: Record<string, string> = { authorization: `Bearer ${this.projectKey}` };
      if (init.body !== undefined) {
        headers['content-type'] = 'application/json';
      }
      const response = await fetchImpl(`${this.host}${path}`, { body: init.body, headers, method: init.method, signal: controller?.signal });
      return { status: response.status, text: await response.text() };
    } finally {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    }
  }

  async form(formId: string, distinctId: string): Promise<ServedForm> {
    const { status, text } = await this.request(`/v1/forms/${encodeURIComponent(formId)}?distinct_id=${encodeURIComponent(distinctId)}`, { method: 'GET' });
    if (status < 200 || status >= 300) {
      throw new TransportError(`form request failed with status ${status}`, status);
    }
    return JSON.parse(text) as ServedForm;
  }

  async submit(formId: string, submission: FormSubmission): Promise<FormAfterSubmit> {
    const { status, text } = await this.request(`/v1/forms/${encodeURIComponent(formId)}/submissions`, { body: JSON.stringify(submission), method: 'POST' });
    if (status >= 200 && status < 300) {
      return (JSON.parse(text) as { afterSubmit: FormAfterSubmit }).afterSubmit;
    }
    let fields: FieldRefusal[] = [];
    let message = `form submission failed with status ${status}`;
    try {
      const parsed = JSON.parse(text) as { error?: { fields?: FieldRefusal[]; message?: string } };
      fields = parsed.error?.fields ?? [];
      message = parsed.error?.message ?? message;
    } catch {
      fields = [];
    }
    throw new FormRefusedError(status, fields, message);
  }
}
